/* eslint-disable @typescript-eslint/no-explicit-any */
import { FC, useContext, useEffect, useRef, useState, useMemo, createContext } from 'react';
import clsx from 'clsx';
import Glide from '@glidejs/glide';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { Heading, Text, Types } from '@marriott/mi-ui-library';
/* eslint-disable @nx/enforce-module-boundaries */
import { PropertyCard } from '@marriott/mi-shop-components';
import { StyledGroupDiningComponent } from './GroupDiningComponent.styles';
import { StyledPropertyCardContainer } from '../SearchResults/SearchOutletCard/SearchOutletCard.styles';
import { GroupDiningComponentProps, GroupDiningRestaurantEdge } from './GroupDiningComponent.types';
import { groupDiningMock as groupDiningMockData } from './__mocks__/GroupDiningComponent.mock';
import { TRACKING_CONSTANT, IMAGE_URL_DOMAIN, BESTOF_RESULT_LIMIT } from '../../modules/utils/constants';
import {
  bestOfFilters,
  convertRangeLabelsToBoundaries,
  getDirection,
  getDiningLabel,
  getOutletShortAddress,
  getSubDirectoryLocale,
  getTitleIconOutlet,
  useGetBreakpoint,
} from '../../modules/utils/helpers';
import { PageParamsContext } from '../../modules/context/PageContext';
import { useLazyQuery } from '@apollo/client';
import { groupDiningDetails } from '@marriott/mi-awp-graphql';
import Cookies from 'js-cookie';
import { SkeletonCarouselLoader } from '../BestOfComponentSkeleton/BestOfComponentSkeleton';

const { size, tags } = Types;
const { NEXT_PUBLIC_AEM_SITE } = process.env;

// ─── AEM editable config ──────────────────────────────────────────────────────

const GroupDiningComponentConfig = {
  emptyLabel: 'groupdining',
  isEmpty: false,
  resourceType: `${NEXT_PUBLIC_AEM_SITE}/components/content/groupdining`,
};

export const GroupDiningComponentEditable = (props: any) => (
  <EditableComponent config={GroupDiningComponentConfig} {...props}>
    <GroupDiningComponent {...props} />
  </EditableComponent>
);

// ─── Component ───────────────────────────────────────────────────────────────

export const GroupDiningComponent: FC<GroupDiningComponentProps> = props => {
  const {
    title,
    description,
    openInNewTab = false,
    isAuthorMode = false,
    trackingProperties,
    styleclass = '',
    // iconPathRestaurant,
    // iconPathBar,
    fileReferenceImage,
    callToReserveLinkLabel,
    callToReserveOpenInNewTab,
    viewDetailsLabel,
    hidePriceIndicator,
    restaurantCapacity,
    privateDiningRoom,
    privateDiningRoomCapacity,
    groupDiningBadgeLabel,
    privateDiningBadgeLabel,
    groupAndPrivateDiningBadgeLabel,
    componentId,
    longitude,
    latitude,
    seating,
    experiences,
  } = props;

  const { currentLocale } = useContext(PageParamsContext);
  const subLocale = getSubDirectoryLocale(currentLocale || 'en-US');
  const parentGlide = useRef<any>();
  const innerGlideList = useRef<any>([]);
  const glideRefOutlet = useRef<HTMLDivElement | any>(null);
  const direction = getDirection();
  const [isFirstSlide, setIsFirstSlide] = useState(true);
  const [isLastSlide, setIsLastSlide] = useState(false);
  const [isMobileViewPort, setIsMobileViewPort] = useState(useGetBreakpoint() === 'mobile');
  const [restaurants, setRestaurants] = useState<GroupDiningRestaurantEdge[]>(
    groupDiningMockData?.data?.searchRestaurantsByGeolocation?.edges
  );
  const { SEARCH_RESULT_CARD, RESERVE_BUTTON, RESTAURANT_DETAILS_BUTTON, INTERNAL_LINK, EXTERNAL_LINK } =
    TRACKING_CONSTANT;
  const [isLoader, setIsLoader] = useState(true);
  const pageContext = useContext(createContext<any>({}));
  const requestId = useMemo(() => {
    return pageContext.requestId;
  }, [pageContext]);
  const sessionID = Cookies.get('sessionID');
  const currentTimestamp = Date.now();
  const currentDateTimeStamp = new Date(currentTimestamp).getTime();
  const [getGroupDiningDetails] = useLazyQuery(groupDiningDetails);
  const hasRequiredGroupDiningProps = !!seating || !!experiences || privateDiningRoom;

  useEffect(() => {
    function handleResize() {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      setIsMobileViewPort(useGetBreakpoint() === 'mobile');
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── Author-mode / mock data ───────────────────────────────────────────────
  useEffect(() => {
    if (isAuthorMode) {
      const mockResult = groupDiningMockData?.data?.searchRestaurantsByGeolocation;
      setRestaurants(mockResult?.edges ?? []);
    }
  }, [isAuthorMode]);
  useEffect(() => {
    if (latitude && longitude && !isAuthorMode && hasRequiredGroupDiningProps) {
      setIsLoader(true);
      const groupDiningRanges =
        privateDiningRoom === true
          ? [
              {
                type: 'capacity',
                endpoints: convertRangeLabelsToBoundaries(restaurantCapacity || []),
              },
              {
                type: 'privateDiningRoomCapacity',
                endpoints: convertRangeLabelsToBoundaries(privateDiningRoomCapacity || []),
              },
            ]
          : [];
      getGroupDiningDetails({
        fetchPolicy: 'network-only',
        ssr: false,
        errorPolicy: 'all',
        variables: {
          search: {
            latitude: latitude,
            longitude: longitude,
            facets: {
              terms: bestOfFilters(props),
              ranges: groupDiningRanges,
            },
          },
          limit: BESTOF_RESULT_LIMIT,
        },
        context: {
          headers: {
            'accept-language': currentLocale?.replace('_', '-') ?? 'en-US',
            'x-request-id': requestId,
            'x-b3-traceId': `${sessionID ?? sessionID ?? 'fallback-token'} - ${currentDateTimeStamp}`,
            'x-b3-spanId': requestId !== '' ? requestId : `${currentDateTimeStamp}`,
            'correlation-id': `${sessionID ?? sessionID ?? 'fallback-token'} - ${currentDateTimeStamp}`,
          },
        },
        onCompleted: data => {
          setIsLoader(false);
          if (data?.searchRestaurantsByGeolocation?.edges && data?.searchRestaurantsByGeolocation?.edges?.length > 0) {
            setRestaurants(data?.searchRestaurantsByGeolocation?.edges);
          } else {
            setRestaurants([]);
          }
        },
        onError: () => {
          setIsLoader(false);
          setRestaurants([]);
        },
      });
    } else {
      // If required inputs are missing, don't show loader
      setIsLoader(false);
    }
  }, [latitude, longitude, isAuthorMode, hasRequiredGroupDiningProps]);

  useEffect(() => {
    if (restaurants?.length > 0) {
      const glideContainer = glideRefOutlet?.current;
      if (!glideContainer) return;

      if (!parentGlide?.current?.type) {
        parentGlide.current = new Glide(glideContainer, {
          type: 'slider',
          gap: 0,
          startAt: 0,
          focusAt: 'center',
          autoplay: false,
          bound: false,
          rewind: false,
          dragThreshold: false,
          direction: direction,
          perView: 1,
          peek: {
            before: 0,
            after: 0,
          },
          keyboard: false,
        });

        const glideOnRun = () => {
          const index = Number(
            document
              .getElementsByClassName('glide__slide outer-glide glide__slide--active')[0]
              ?.getAttribute('data-glide-index')
          );

          if (Number(index) === 0 && direction === 'rtl') {
            setIsFirstSlide(true);
            setIsLastSlide(false);
          } else if (Number(index) === restaurants?.length - 1 && direction === 'rtl') {
            setIsFirstSlide(false);
            setIsLastSlide(true);
          } else {
            setIsFirstSlide(false);
            setIsLastSlide(false);
          }

          if (index !== null && restaurants) {
            const nextIndex = (index + 1) % restaurants?.length;
            const prevIndex = (index - 1 + restaurants?.length) % restaurants?.length;
            const nextImg = glideRefOutlet?.current?.querySelector(`[data-glide-index='${nextIndex}'] img`);
            const prevImg = glideRefOutlet?.current?.querySelector(`[data-glide-index='${prevIndex}'] img`);

            if (nextImg) nextImg?.setAttribute('loading', 'eager');
            if (prevImg) prevImg?.setAttribute('loading', 'eager');
          }
        };

        parentGlide?.current?.on('run.after', glideOnRun);
      }
    }
  }, [restaurants]);

  useEffect(() => {
    let innerGlideFix;
    if (restaurants?.length > 0) {
      if (parentGlide?.current && glideRefOutlet?.current) {
        // Compute count directly from current restaurants to avoid stale state
        let innerCarouselCount = 0;
        restaurants?.forEach((edge: any) => {
          if (edge?.node?.media?.imageConnection?.total > 1) {
            let wideHorizCount = 0;
            edge?.node?.media?.imageConnection?.edges?.forEach((img: any) => {
              if (img?.node?.imageUrls?.wideHorizontal) wideHorizCount++;
            });
            if (wideHorizCount > 1) innerCarouselCount++;
          }
        });
        innerGlideFix = innerGlideList?.current?.filter((instance: any) => instance);
        if (innerGlideFix?.length === innerCarouselCount) {
          innerGlideList?.current?.forEach((childGlide: any) => {
            childGlide?.on('move', function () {
              parentGlide?.current?.disable();
            });
            childGlide?.on('move.after', function () {
              parentGlide?.current?.enable();
            });
            childGlide?.mount();
          });
          // HACK FOR CHILD PIP
          parentGlide?.current?.on('run.after', () => {
            innerGlideList?.current?.forEach((childGlide: any) => {
              document
                .querySelector(`${childGlide.selector} [data-glide-el="controls[nav]"] button.glide__bullet--active`)
                ?.classList.remove('glide__bullet--active');
              document
                .querySelector(`${childGlide.selector} [data-glide-el="controls[nav]"] button`)
                ?.classList.add('glide__bullet--active');
            });
          });
          parentGlide?.current?.mount();
        } else if (innerGlideList?.current?.length === 0) {
          parentGlide?.current?.mount();
        }
      }
    }
  }, [restaurants]);

  const getOutletTitle = (restaurantNode: any): string => {
    if (restaurantNode?.description) {
      return restaurantNode?.description.length > 242
        ? restaurantNode.description.slice(0, 242) + '[...]'
        : restaurantNode.description;
    }
    const fallback = restaurantNode?.description;
    return fallback.length > 242 ? fallback.slice(0, 242) + '[...]' : fallback;
  };

  const getOutletName = (restaurantNode: any) => {
    return {
      titleDetails: {
        title: restaurantNode?.name ?? '',
        titleAriaLabel: restaurantNode?.name ?? '',
      },
    };
  };

  const getOutletImages = (
    restaurantNode: any
  ): { outletImages: Array<{ defaultImageUrl: string }>; isFallBackImage: boolean } => {
    let isFallBackImage = false;
    const images: { defaultImageUrl: string } = { defaultImageUrl: '' };
    let defaultImageUrl = '';
    const outletImages: Array<{ defaultImageUrl: string }> = [];

    const sortedImages = [...(restaurantNode?.media?.imageConnection?.edges ?? [])].sort(
      (a: any, b: any) =>
        Number(b?.node?.isPrimaryArchitectural) - Number(a?.node?.isPrimaryArchitectural) ||
        Number(b?.node?.isPrimaryServices) - Number(a?.node?.isPrimaryServices) ||
        Number(b?.node?.isSecondaryArchitectural) - Number(a?.node?.isSecondaryArchitectural) ||
        Number(b?.node?.isSecondaryServices) - Number(a?.node?.isSecondaryServices)
    );
    const trimmedImages = sortedImages?.slice(0, 5);
    trimmedImages?.forEach((element: any) => {
      if (element?.node?.imageUrls?.wideHorizontal) {
        defaultImageUrl = `${IMAGE_URL_DOMAIN}${element?.node?.imageUrls?.wideHorizontal}?downsize=504:*`;
        images['defaultImageUrl'] = defaultImageUrl;
        outletImages.push({ ...images });
      }
    });

    // Fallback to dynamicMedia or fileReferenceImage when no real images were found
    if (outletImages?.length === 0) {
      if (fileReferenceImage) {
        outletImages.push({ defaultImageUrl: `${fileReferenceImage}?downsize=504:*` });
        isFallBackImage = true;
      }
    }

    return { outletImages, isFallBackImage };
  };

  const getTertiaryLinks = (restaurantNode: any) => {
    const phoneNo =
      restaurantNode?.contactInfo?.contactNumbers &&
      restaurantNode?.contactInfo?.contactNumbers?.length > 0 &&
      restaurantNode?.contactInfo?.contactNumbers[0]?.phoneNumber?.display &&
      !restaurantNode?.contactInfo?.contactNumbers[0]?.phoneNumber?.display.includes(undefined)
        ? restaurantNode?.contactInfo?.contactNumbers[0]?.phoneNumber?.display
        : '';
    if (phoneNo) {
      return {
        href: `tel:${phoneNo}`,
        target: callToReserveOpenInNewTab ? '_blank' : '_self',
        custom_click_track_value: `${SEARCH_RESULT_CARD}|${RESERVE_BUTTON} on ${restaurantNode?.name}|${EXTERNAL_LINK}`,
        rel: 'noreferrer',
        className: 'm-link-tertiary-button custom_click_track my-2',
        buttonCopy: isMobileViewPort ? callToReserveLinkLabel : `${callToReserveLinkLabel} ${phoneNo}`,
        isLink: true,
      };
    }
    return {};
  };

  const getFooterLinks = (restaurantNode: any) => {
    if (!restaurantNode?.seoUrl) return undefined;
    return {
      href: `${subLocale}/dining/${restaurantNode.seoUrl}.mi`,
      target: openInNewTab ? '_blank' : '_self',
      ratesClickTrackValue: `${SEARCH_RESULT_CARD}|${RESTAURANT_DETAILS_BUTTON}|${INTERNAL_LINK}`,
      rel: 'noreferrer',
      className: 'm-button-s m-button-primary custom_click_track reserve-cta-button',
      text: viewDetailsLabel,
      isLink: false,
    };
  };

  const getEyeBrowDetails = (restaurantNode: any): string => {
    let eyeBrowText = restaurantNode?.cuisine ?? '';
    eyeBrowText =
      !hidePriceIndicator && restaurantNode?.diningExperience?.affordabilityPriceIndicator != null
        ? eyeBrowText + ` | ${restaurantNode?.diningExperience?.affordabilityPriceIndicator ?? ''}`
        : eyeBrowText;

    return eyeBrowText;
  };

  if (isLoader) {
    return <SkeletonCarouselLoader />;
  }
  return restaurants?.length > 0 ? (
    <StyledGroupDiningComponent
      data-testid="group-dining-component"
      data-component-name="o-awp-groupdiningcomponent"
      className={clsx('py-5 m-container-fullbleed', styleclass)}
    >
      <div className="container">
        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div className="group-dining__header">
          <div className="group-dining__title-block">
            {title && <Heading variation={Types.headingType.title} fontSize={size.small} titleText={title} />}
            {description && (
              <Text
                element={tags.paragraph}
                copyText={description}
                customClass="t-font-s"
                fontSize={Types.size.small}
              />
            )}
          </div>
        </div>

        <div id={componentId} className="glide glide-group-dining" ref={glideRefOutlet}>
          <div className="glide__track" data-glide-el="track">
            <ul className="glide__slides d-flex p-0 mb-0 outer-glide">
              {restaurants?.map((edge, index) => {
                const node = edge?.node;
                const { outletImages } = getOutletImages(node) as any;
                const { titleDetails } = getOutletName(node);
                const iconTitle = getTitleIconOutlet(node as any);
                const ribbonText = getDiningLabel(
                  node?.diningExperience?.experiences || [],
                  node?.diningExperience?.seatings || [],
                  !!node?.diningExperience?.hasPrivateDiningRoom,
                  groupAndPrivateDiningBadgeLabel || '',
                  groupDiningBadgeLabel || '',
                  privateDiningBadgeLabel || ''
                );
                const ribbonClass = 'w-100';
                const tertiaryLinkDetails = getTertiaryLinks(node);

                const PropertyCardProps = {
                  titleDetails,
                  eyebrow: getEyeBrowDetails(node),
                  description: getOutletTitle(node),
                  carouselDotsPosition: 'center',
                  images: outletImages,
                  ...(Object.keys(tertiaryLinkDetails || {}).length > 0 ? { tertiaryLinkDetails } : {}),
                  footerLinkDetails: getFooterLinks(node),
                  showIcon: true,
                  iconTitle,
                  address: getOutletShortAddress(node?.contactInformation ?? node?.contactInfo, props) || '',
                  propertyName: node?.property?.basicInformation?.name || '',
                  isVerticalCard: false,
                  trackingProperties,
                  parentGlide: parentGlide.current,
                  innerGlideList,
                  glideClass: `child-glide-group-dining-${index}`,
                  slideIndex: index + 1,
                  ribbonDetails: ribbonText
                    ? {
                        ribbonText,
                        ribbonClass,
                      }
                    : undefined,
                  variation: 'culinaryView',
                };

                return (
                  <li
                    className="glide__slide outer-glide"
                    key={`group-dining-slide-${node?.id ?? index}`}
                    data-glide-index={index}
                  >
                    <StyledPropertyCardContainer>
                      <PropertyCard {...PropertyCardProps} />
                    </StyledPropertyCardContainer>
                  </li>
                );
              })}
            </ul>
          </div>

          {restaurants?.length > 1 && (
            <div
              className={clsx('d-flex align-items-center justify-content-center controls_wrapper')}
              data-testid={componentId}
            >
              <div className={clsx(isMobileViewPort ? 'carouselControlType3b' : 'carouselControlType2')}>
                <div className="glide__arrows" data-glide-el="controls">
                  <button
                    className={`glide__arrow--left left-arrow ${
                      isFirstSlide && direction === 'rtl' ? 'disabled-rtl-arrow' : 'enabled-rtl-arrow'
                    }  ${direction === 'rtl' ? 'ml-0 ml-sm-4' : ''}`}
                    aria-label={props?.previousButtonLabel}
                    data-content={props?.previousButtonLabel}
                    id={`${componentId}_left_arrow`}
                    data-glide-dir={`${direction === 'rtl' ? '>' : '<'}`}
                  >
                    <span className="icon-arrow-left"></span>
                  </button>
                </div>
                <div data-glide-el="controls[nav]">
                  {restaurants?.map((_, index: number) => {
                    return (
                      <button
                        className=""
                        data-glide-dir={`=${index}`}
                        key={`featured-outlet-controls-${index}`}
                        aria-label={`Slide ${index}`}
                      ></button>
                    );
                  })}
                </div>
                <div className="glide__arrows" data-glide-el="controls">
                  <button
                    className={` glide__arrow--right right-arrow ${
                      isLastSlide && direction === 'rtl' ? 'disabled-rtl-arrow' : 'enabled-rtl-arrow'
                    } ${direction === 'rtl' ? 'mr-0 mr-sm-4' : ''} ${
                      currentLocale === 'ru-RU' ? 'ru-locale-specific' : ''
                    }`}
                    data-glide-dir={`${direction === 'rtl' ? '<' : '>'}`}
                    aria-label={props?.nextButtonLabel}
                    id={`${componentId}_right_arrow`}
                    data-content={props?.nextButtonLabel}
                  >
                    <span className="icon-arrow-right"></span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </StyledGroupDiningComponent>
  ) : null;
};
